import styled from '@emotion/styled'
import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import { useUserMe } from 'api/goodtrust/user'
import { ActionCards } from 'components/actions/ActionCards'
import { useLPActions } from 'components/actions/useLPActions'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { Card } from 'components/content/card/Card'
import { CenteredTextImageSection } from 'components/content/centeredTextImageSection/CenteredTextImageSection'
import { GoodTrustReferences } from 'components/estatePlanning/landing/references/GoodTrustReferences'
import { EstatePlanningSignupSection } from 'components/estatePlanning/landing/signup/EstatePlanningSignupSection'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Icon, IconName } from 'components/icon'
import { ContentfulImage } from 'components/Image'
import { LandingPageTextImagePerks } from 'components/landingPageTextImageSection/LandingPageTextImagePerks'
import { Layout } from 'components/layout/Layout'
import { LandingPageHero } from 'components/landingPageHero/LandingPageHero'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { SecuritySection } from 'components/security/SecuritySection'
import { Spacer } from 'components/spacer/Spacer'
import { Stack } from 'components/Stack'
import { SupportSection } from 'components/supportSection/SupportSection'
import { TestimonialsSection } from 'components/testimonialsSection/TestimonialsSection'
import { Text, Title, Title_h3_default } from 'components/Typography'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { colors } from 'utils/colors'
import { REVALIDATE_SECS } from 'utils/constants'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { numberRange } from 'utils/general'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { mapKeysToTrans } from 'utils/i18n/mapKeysToTrans'
import { NumberRange } from 'utils/types'
import { ContentfulMentions, getContentfulMentions } from 'api/contentful/getContentfulMentions'

interface DigitalVaultPageStaticProps {
  mediaMentions: ContentfulMediaMentions
  mentions: ContentfulMentions
  trustSectionMediaMentions: ContentfulMediaMentions
  faq: ContentfulFAQ
}

const STitle = styled(Title)`
  white-space: pre-line;
  text-align: center;
  margin-bottom: 1rem;
  b {
    font-weight: 600;
  }
`

const featureSectionIcons: Record<NumberRange<4>, IconName> = {
  '1': 'ShieldCheck',
  '2': 'TextDocument',
  '3': 'PhoneLock',
  '4': 'PersonSimple',
}

export default function Page(props: DigitalVaultPageStaticProps) {
  const images = useContentfulImages()
  const { t } = useTranslation('digital_vault')
  const actions = useLPActions([
    'online_accounts',
    'passcodes_devices',
    'documents',
    'trusted_contacts',
  ])
  const { faq } = props

  const userSwr = useUserMe()
  const setupDigitalVaultHref = userSwr.data ? '/me/digital-vault' : '/signup'

  const heroPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `digital_vault.hero.perk_${i}` as const)
  )

  return (
    <Layout
      header={<TopNavigationLandingPage variant="general-info" noSpacingBelow />}
      footer={<Footer variant="estate-planning" />}
    >
      <PageTitle title={t('digital_vault.meta_title')} />
      <PageMeta description={t('digital_vault.meta_description')} />

      <LandingPageHero
        title={t('digital_vault.hero.title')}
        image={<ContentfulImage {...images.digital_vault_hero} />}
        className="desktop-only"
        css={{ background: colors.blue[75] }}
        heroCSS={{ gridColumnGap: 0 }}
      >
        <Stack space="2rem">
          <LandingPageTextImagePerks perks={heroPerks} simple iconColor={colors.blue[500]} />
          <Button
            type="button"
            href={setupDigitalVaultHref}
            size={['medium', 'large']}
            renderAsButton
          >
            {t('digital_vault.hero.cta')}
          </Button>
          <GoodTrustReferences
            caption={t('digital_vault.hero.mentions_caption')}
            mediaMentions={props.mediaMentions}
            responsiveItemCounts={[4, 4, 4]}
          />
        </Stack>
      </LandingPageHero>

      <div className="mobile-only">
        <STitle variant={Title_h3_default} as="h1">
          {t('digital_vault.hero.title')}
        </STitle>
        <ContentfulImage {...images.digital_vault_hero_mobile} />
        <Spacer size="1.5rem" />

        <Block>
          <Stack space="1.5rem">
            <LandingPageTextImagePerks perks={heroPerks} />
            <Button type="button" href="/me/dashboard" size={['medium', 'large']} renderAsButton>
              {t('digital_vault.hero.cta')}
            </Button>
            <GoodTrustReferences
              caption={t('digital_vault.hero.mentions_caption')}
              mediaMentions={props.mediaMentions}
              responsiveItemCounts={[4, 4, 4]}
            />
          </Stack>
        </Block>
      </div>

      <Spacer size={[4, 3]} />

      <Block>
        <ActionCards
          variant="round-button"
          title={t('digital_vault.action_cards_title')}
          actions={actions}
        />
      </Block>

      <Spacer size={[2, 6]} />

      <CenteredTextImageSection
        title={t('digital_vault.section_feature_list.title')}
        image={<ContentfulImage {...images.digital_vault_section_vault} />}
        action={
          <Button type="button" size={['medium', 'large']} href={setupDigitalVaultHref}>
            {t('digital_vault.section_feature_list.cta')}
          </Button>
        }
        imagePlacement="right"
      >
        <Stack orientation="vertical" space="1rem">
          {numberRange(4).map((i) => (
            <Card
              icon={<Icon name={featureSectionIcons[i]} style={{ marginRight: 0 }} />}
              key={i}
              title={t(`digital_vault.section_feature_list.item_${i}.title`)}
            >
              <Text>{t(`digital_vault.section_feature_list.item_${i}.desc`)}</Text>
            </Card>
          ))}
        </Stack>
      </CenteredTextImageSection>

      <Spacer size={[3, 6]} />

      <SecuritySection />

      <Spacer size={[3, 6]} />

      <SupportSection />

      <Spacer size={[3, 6]} />

      <TestimonialsSection
        mediaMentions={props.trustSectionMediaMentions}
        responsiveItemCounts={[4, 4, 4]}
        mentions={props.mentions}
      />

      <Spacer size={[3, 6]} />

      <FaqTwoColumns content={faq} />

      <Spacer size={[3, 6]} />

      <EstatePlanningSignupSection mediaMentions={props.mediaMentions} place="digital_vault_lp" />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps<DigitalVaultPageStaticProps> = async (ctx) => {
  const mediaMentions = await getContentfulMediaMentions('global')
  const mentions = await getContentfulMentions('desktop')

  const trustSectionMediaMentions = await getContentfulMediaMentions('global')
  const faq = await getContentfulFAQ('digital-vault')

  if (!mediaMentions) throw Error('Could not fetch Digital Vault page content.')

  const props: DigitalVaultPageStaticProps = {
    ...(await getContentfulImages()),
    ...(await getIntlProps(ctx, ['digital_vault', 'estate_planning_lp'])),
    mediaMentions,
    mentions,
    trustSectionMediaMentions,
    faq,
  }

  return { props, revalidate: REVALIDATE_SECS }
}
